import { ChevronRight } from "./icons";
import SettingsToggle from "./SettingsToggle";

/**
 * One row in a settings card.
 *
 * Rows draw their own top divider (see style.css), so callers never need to
 * know a row's position within the card.
 *
 * `right` controls the trailing affordance:
 *   "none" (default) — the row navigates straight to a page, or is read-only.
 *                      Deliberately chevron-less: a chevron here would promise a
 *                      sub-level that does not exist.
 *   "expand"         — the row has children; renders a chevron that rotates to
 *                      point down while `expanded`.
 *   "toggle"         — renders SettingsToggle; the row itself is not clickable.
 */
function SettingsRow({
  icon,
  label,
  value,
  badge,
  right = "none",
  checked,
  onChange,
  onClick,
  expanded = false,
  nested = false,
  danger = false,
  disabled = false,
  className = "",
}) {
  const isToggle = right === "toggle";
  const isInteractive = !isToggle && typeof onClick === "function" && !disabled;

  const classes = [
    "aris-settings-row",
    isInteractive && "aris-settings-row--interactive",
    nested && "aris-settings-row--nested",
    expanded && "aris-settings-row--expanded",
    danger && "aris-settings-row--danger",
    disabled && "aris-settings-row--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {icon && !danger && <span className="aris-settings-row__icon">{icon}</span>}

      <span className="aris-settings-row__text">
        <span className="aris-settings-row__label">{label}</span>
        {value != null && value !== "" && (
          <span className="aris-settings-row__value">{value}</span>
        )}
      </span>

      {badge > 0 && <span className="aris-settings-row__badge">{badge}</span>}

      {isToggle && (
        <SettingsToggle
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          label={typeof label === "string" ? label : undefined}
        />
      )}

      {right === "expand" && (
        <span className="aris-settings-row__chevron">
          <ChevronRight />
        </span>
      )}
    </>
  );

  // A toggle row contains its own button, so the row must not also be one.
  if (isToggle) {
    return <div className={classes}>{body}</div>;
  }

  if (!isInteractive) {
    return <div className={classes}>{body}</div>;
  }

  return (
    <button type="button" className={classes} onClick={onClick}>
      {body}
    </button>
  );
}

export default SettingsRow;
